import {
  FormProp,
  FormProps,
  ConfigField,
  ValidationConfig,
  Validity
} from '../types/index';

export function getFieldValidityState(fieldname: string, isMatch: boolean = true, message: string = '', isTouched: boolean = false): Validity {
  return {
    [fieldname]: {
      isValid: isMatch,
      message: isMatch ? '' : message,
      isTouched,
      isError: !isMatch && isTouched
    }
  }
}

export function isRequiredMatch(formField: FormProp): boolean {
  return !!formField?.value;
}

export function isRequiredTrueMatch(formField: FormProp): boolean {
  return formField?.value === true && typeof formField?.value === 'boolean'
}

export function isRequiredValueMatch(formField: FormProp, configField: ConfigField): boolean {
  return formField?.value === configField?.value;
}

export function isMinMatch(formField: FormProp, configField: ConfigField):boolean {
  return !!configField?.value && formField?.value >= configField?.value;
}

export function isMaxMatch(formField: FormProp, configField: ConfigField): boolean {
  return !!configField?.value && formField?.value <= configField.value;
}

export function isEmailMatch(formField: FormProp): boolean {
  // regExp for email
  return typeof formField?.value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formField?.value);
}

export function isMinLengthMatch(formField: FormProp, configField: ConfigField): boolean {
  return !!configField?.value && typeof formField?.value === 'string' && formField?.value.length >= configField.value;
}

export function isMaxLengthMatch(formField: FormProp, configField: ConfigField): boolean {
  return !!configField?.value && typeof formField.value === 'string' && formField.value.length <= configField.value;
}

export function isPatternMatch(formField: FormProp, configField: ConfigField): boolean {
  return configField?.value instanceof RegExp && typeof formField?.value === 'string' && configField.value?.test(formField.value);
}


export function isEqualMatch(formField: FormProp, configField: ConfigField, formProps: FormProps): boolean {
  return !!configField?.fieldname && formField?.value === formProps[configField.fieldname]?.value;
}

export function isBiggerThanMatch(formField: FormProp, configField: ConfigField, formProps: FormProps): boolean {
  if (!isNumber(formField?.value)) return false;
  return !!configField?.fieldname && formField?.value > formProps[configField.fieldname]?.value;
}

export function isLowerThanMatch(formField: FormProp, configField: ConfigField, formProps: FormProps): boolean {
  if (!isNumber(formField?.value)) return false;
  return !!configField?.fieldname && formField?.value < formProps[configField.fieldname]?.value;
}

export function checkFormValidity(validationResult: Validity): boolean {
  return Object.values(validationResult).every(field => field.isValid)
}

export function checkConfigNumber(config: ConfigField, fieldName: string, rule: string): void {
  const value = config?.value;
  if(!isNumber(value)) {
    throw new Error(`Validation rule "${rule}" for field "${fieldName}" has to be number in config object`)
  }
}

export function isNumber(item: string | number | boolean | RegExp | undefined): boolean {
  const result = (!(item instanceof RegExp
    || (!item && item !== 0)
    || typeof item === 'boolean'
    || (typeof item === 'string' && isNaN(parseInt(item)))));

  return result
}

export function validateForm(validationConfig: ValidationConfig, formProps: FormProps): { validity: Validity, isFormValid: boolean } {
  const allFieldsValidationResult = Object.keys(validationConfig.fields).reduce((acc, fieldname) => {
    const formField = formProps[fieldname];
    const configField = validationConfig.fields[fieldname];

    let validationResult: Validity;

    if (!!configField?.required?.message.length && !isRequiredMatch(formField)) {
      validationResult = getFieldValidityState(fieldname, isRequiredMatch(formField), configField.required.message, formField?.isTouched);
    }
    else if (!!configField?.requiredTrue?.message.length && !isRequiredTrueMatch(formField)) {
      validationResult = getFieldValidityState(fieldname, isRequiredTrueMatch(formField), configField.requiredTrue.message, formField?.isTouched);
    }
    else if (!!configField?.requiredValue?.message.length && !isRequiredValueMatch(formField, configField.requiredValue)) {
      validationResult = getFieldValidityState(fieldname, isRequiredValueMatch(formField, configField.requiredValue), configField.requiredValue.message, formField?.isTouched);
    }
    else if (!!configField?.min?.message.length && !isMinMatch(formField, configField.min)) {
      checkConfigNumber(configField.min, fieldname, 'min');
      validationResult = getFieldValidityState(fieldname, (isMinMatch(formField, configField.min) || !formField?.value), configField.min.message, formField?.isTouched);
    }
    else if (!!configField?.max?.message.length && !isMaxMatch(formField, configField.max)) {
      checkConfigNumber(configField.max, fieldname, 'max');
      validationResult = getFieldValidityState(fieldname, (isMaxMatch(formField, configField.max) || !formField?.value), configField.max.message, formField?.isTouched);
    }
    else if (!!configField?.email?.message.length && !isEmailMatch(formField)) {
      validationResult = getFieldValidityState(fieldname, (isEmailMatch(formField) || !formField?.value), configField.email.message, formField?.isTouched);
    }
    else if (!!configField?.minLength?.message.length && !isMinLengthMatch(formField, configField.minLength)) {
      checkConfigNumber(configField.minLength, fieldname, 'minLength');
      validationResult = getFieldValidityState(fieldname, (isMinLengthMatch(formField, configField.minLength) || !formField?.value), configField.minLength.message, formField?.isTouched);
    }
    else if (!!configField?.maxLength?.message.length && !isMaxLengthMatch(formField, configField.maxLength)) {
      checkConfigNumber(configField.maxLength, fieldname, 'maxLength');
      validationResult = getFieldValidityState(fieldname, (isMaxLengthMatch(formField, configField.maxLength) || !formField?.value), configField.maxLength.message, formField?.isTouched);
    }
    else if (!!configField?.pattern?.message.length && !isPatternMatch(formField, configField.pattern)) {
      validationResult = getFieldValidityState(fieldname, (isPatternMatch(formField, configField.pattern) || !formField?.value), configField.pattern.message, formField?.isTouched);
    }
    else if (!!configField?.equal?.message.length && !isEqualMatch(formField, configField.equal, formProps)) {
      validationResult = getFieldValidityState(fieldname, (isEqualMatch(formField, configField.equal, formProps) || !formField?.value), configField.equal.message, formField?.isTouched);
    }
    else if (!!configField?.notEqual?.message.length && !isEqualMatch(formField, configField.equal, formProps)) {
      validationResult = getFieldValidityState(fieldname, !isEqualMatch(formField, configField.notEqual, formProps), configField.notEqual.message, formField?.isTouched);
    }
    else if (!!configField?.biggerThan?.message.length && !isBiggerThanMatch(formField, configField.biggerThan, formProps)) {
      validationResult = getFieldValidityState(fieldname, (isBiggerThanMatch(formField, configField.biggerThan, formProps) || !formField?.value), configField.biggerThan.message, formField?.isTouched);
    }
    else if (!!configField?.lowerThan?.message.length && !isLowerThanMatch(formField, configField.lowerThan, formProps)) {
      validationResult = getFieldValidityState(fieldname, (isLowerThanMatch(formField, configField.lowerThan, formProps) || !formField?.value), configField.lowerThan?.message, formField?.isTouched);
    } else {
      validationResult = getFieldValidityState(fieldname, true, '', formField?.isTouched);
    }

    acc[fieldname] = validationResult[fieldname];
    return acc;

  }, {} as Validity);

  const isFormValid = checkFormValidity(allFieldsValidationResult);

  return { validity: allFieldsValidationResult, isFormValid }
}

